import React, { useState, useEffect, Fragment } from 'react';
import {
	AlertBox,
	Button,
	Dialog,
	DialogContent,
	DialogFooter,
	ExtendedTheme,
	Heading,
	Loader,
	TextBlock
} from 'neon-design-system';
import { createStyles, makeStyles, Box, Grid, Paper } from '@material-ui/core';
import { Elements, PaymentElement, ElementsConsumer } from '@stripe/react-stripe-js';
import { Stripe, StripeElements } from '@stripe/stripe-js';
import { stripeCustomFont, stripeFormAppearance, stripeFormOptions, stripePromise } from '../../utils/stripe';
import { StripePaymentSetupIntent, CREATE_STRIPE_PAYMENT_SETUP_INTENT } from '../../gql/users'
import { useMutation, useQuery } from '@apollo/react-hooks';
import { baseUrls } from '../../utils/baseUrls.utils';
import { ISO_COUNTRIES } from '../../gql/countries';
import { IsoCountry } from '../../reference-data/country-iso-codes';

const useDialogStyles = makeStyles((theme: ExtendedTheme) => createStyles({
	content: {
		width: 468,
		minHeight: 120,
		[theme.breakpoints.down('xs')]: {
			width: '100%'
		}
	},
	panel: {
		background: theme.themeColors.grey[700],
		padding: theme.spacing(2)
	},
	spread: {
		display: 'flex',
		justifyContent: 'space-between',
		alignItems: 'top',
		textAlign: 'right'
	},
	spacer: {
		height: 1,
		background: theme.themeColors.grey[600]
	}
}));

function filter(array: (string | undefined)[]) {
	return array.filter(t => !!t) as string[];
}

interface UpdateCardDialogProps {
	subscriptionId: string;
	isDialogOpen: boolean;
	openDialog?: () => void;
	closeDialog: () => void;
	onStripeCardUpdated: () => void;
}

const UpdateCardDialog: React.FC<UpdateCardDialogProps> = (props) => {
	const { isDialogOpen, closeDialog, subscriptionId, onStripeCardUpdated } = props;
	const classes = useDialogStyles();
	const [stripe, setStripe] = useState<Stripe | null>();
	const [elements, setElements] = useState<StripeElements | null>();
	const [busy, setBusy] = useState(false);
	const [stripeError, setStripeError] = useState<string | undefined>();
	const redirectUrl = `${window.location.origin}/insights${baseUrls.subscriptions}?channels`;
	const { data: isoCountriesResponse } = useQuery<{ isoCountries: IsoCountry[] }>(ISO_COUNTRIES, {fetchPolicy: 'cache-first'});

	useEffect(() => {
		if (isDialogOpen) {
			setBusy(false);
		}
	}, [isDialogOpen]);

	const [stripeSetupIntent, {
		data: setupIntentResponse,
		loading: setupIntentResponseLoading
	}] = useMutation<StripePaymentSetupIntent>(CREATE_STRIPE_PAYMENT_SETUP_INTENT);

	const handlePaymentElementReady = (stripe: Stripe | null, elements: StripeElements | null) => {
		setStripe(stripe);
		setElements(elements);
	}

	const handleSubmit = async (event: { preventDefault: () => void }) => {
		setBusy(true);
		event.preventDefault();

		if (!stripe || !elements) {
			return;
		}

		let billingAddress;

		if (setupIntentResponse?.createPaymentSetupIntent.billingAddress) {
			const {
			// eslint-disable-next-line
				city, country, line1, line2, postalCode: postal_code, state
			} = setupIntentResponse?.createPaymentSetupIntent.billingAddress;
			// eslint-disable-next-line
			billingAddress = {city, country, line1, line2, postal_code, state};
		}

		const { error } = await stripe.confirmSetup({
			elements: elements as any,
			redirect: 'if_required',
			confirmParams: {
				// eslint-disable-next-line
				return_url: redirectUrl,
				// eslint-disable-next-line
				payment_method_data: {
					// eslint-disable-next-line
					billing_details: {
						address: billingAddress
					}
				}
			}
		});

		setBusy(false);

		if (error) {
			setStripeError(error?.message);
		} else {
			onStripeCardUpdated();
			closeDialog();
		}
	};

	useEffect(() => {
		if (setupIntentResponse) return;
		if (!isDialogOpen) return;
		stripeSetupIntent({ variables: { subscriptionId: subscriptionId } });
	}, [setupIntentResponse, stripeSetupIntent, isDialogOpen, subscriptionId]);

	const billingAddress = setupIntentResponse?.createPaymentSetupIntent.billingAddress;
	const metadata = setupIntentResponse?.createPaymentSetupIntent.metadata;
	const country = isoCountriesResponse?.isoCountries.find(c => c.alpha2 === billingAddress?.country)?.name;

	const displayBillingAddress = filter([
		filter([billingAddress?.line1, billingAddress?.line2]).join(', '),
		filter([billingAddress?.city, billingAddress?.postalCode]).join(', '),
		filter([billingAddress?.state, country || billingAddress?.country]).join(', ')
	]).map((x, i) => <Fragment key={i}>{x}<br/></Fragment>);

	return (
		<Dialog
			title="Update payment details"
			maxWidth={500}
			open={isDialogOpen}
		>
			{stripeError && <Box p={2} pt={0}>
				<AlertBox
					visible={!!stripeError}
					styleName="error"
					onClose={() => setStripeError('')}
				>
					{stripeError}
				</AlertBox>
			</Box>}
			<DialogContent>
				<div className={classes.content}>
					{!setupIntentResponse && setupIntentResponseLoading
						? <Loader />
						: <Grid container spacing={2}>
							<Grid item xs={12}>
								<Elements
									stripe={stripePromise}
									options={{
										clientSecret: setupIntentResponse?.createPaymentSetupIntent.clientSecret,
										fonts: stripeCustomFont,
										appearance: stripeFormAppearance
									}}>
									<ElementsConsumer>
										{({ stripe, elements }) => (
											<PaymentElement
												options={stripeFormOptions}
												onReady={() => handlePaymentElementReady(stripe, elements)}
											/>
										)}
									</ElementsConsumer>
								</Elements>
							</Grid>
							<Grid item xs={12}>
								<Paper elevation={0} className={classes.panel}>
									<Grid container spacing={2}>
										<Grid item xs={12}>
											<Heading styleName="headline2">Billing information</Heading>
										</Grid>
										<Grid item xs={12}>
											<div className={classes.spread}>
												<TextBlock styleName="footnote">
													<strong>Business name</strong>
												</TextBlock>
												<TextBlock styleName="footnote">
													{metadata?.businessName}
												</TextBlock>
											</div>
										</Grid>
										<Grid item xs={12}>
											<div className={classes.spacer} />
										</Grid>
										<Grid item xs={12}>
											<div className={classes.spread}>
												<TextBlock styleName="footnote">
													<strong>Tax ID</strong>
												</TextBlock>
												<TextBlock styleName="footnote">
													{metadata?.taxId}
												</TextBlock>
											</div>
										</Grid>
										<Grid item xs={12}>
											<div className={classes.spacer} />
										</Grid>
										<Grid item xs={12}>
											<div className={classes.spread}>
												<TextBlock styleName="footnote">
													<strong>Billing address</strong>
												</TextBlock>
												<TextBlock styleName="footnote">
													{displayBillingAddress}
												</TextBlock>
											</div>
										</Grid>
									</Grid>
								</Paper>
							</Grid>
						</Grid>
					}
				</div>
			</DialogContent>
			<DialogFooter>
				<Button
					id="updatecard-dialog-cancel-button"
					colorType={'grey'}
					onClick={closeDialog}>
					{'Cancel'}
				</Button>
				<Button
					id="updatecard-dialog-submit-button"
					colorType="primary"
					busy={busy}
					onClick={handleSubmit}>
					{'Update and save'}
				</Button>
			</DialogFooter>
		</Dialog >);
}

export default UpdateCardDialog;
